import pdfMake from 'pdfmake';
import figureFormatter from "@/services/utils/figureFormatter";
import handleHospital from "@/services/modules/hospital";
import pdfHeaderFooter from '@/services/utils/pdf/pdfHeaderFooter';
import useDate from '@/services/utils/day';

pdfMake.fonts = {
    SulaimanLipi: {
        normal: 'https://fonts.cdnfonts.com/s/14639/solaimanlipi.woff',
        bold: 'https://cdnjs.cloudflare.com/ajax/libs/pdfmake/0.1.66/fonts/Roboto/Roboto-Medium.ttf',
        italics: 'https://cdnjs.cloudflare.com/ajax/libs/pdfmake/0.1.66/fonts/Roboto/Roboto-Italic.ttf',
        bolditalics: 'https://cdnjs.cloudflare.com/ajax/libs/pdfmake/0.1.66/fonts/Roboto/Roboto-MediumItalic.ttf'
    }
}

const ipdStatementDetails = () => {
    const { commaFormat } = figureFormatter();
    const { dateOfBarthConvertToAge } = handleHospital();
    const { getHeader, getFooter } = pdfHeaderFooter();
    const { formatDateWithTime } = useDate();

    const exportToPDF = (company, ipdRegisterDetails, saleMastersGrouped, userName, barcode, qrcode, receiptGenerals, total) => {

        var doc = {
            pageSize: 'A4',
            pageMargins: [ 25, 100, 25, 80 ],
            header: getHeader(company),
            footer: getFooter(userName, qrcode),
            content: getContent(ipdRegisterDetails, saleMastersGrouped, barcode, receiptGenerals, total),

            styles : {
                header: {
                    fontSize: 24,
                }
            },
            defaultStyle: {
                color: 'black',
                fontSize: 10,
                font: 'SulaimanLipi'
            },
            info: {
                title: 'Daily Sales Report'
            }
        }

        pushTable(saleMastersGrouped, doc)
        doc.content.push(...getSummaryTable(receiptGenerals, total));
        const pdfDocGenerator = pdfMake.createPdf(doc);
        pdfDocGenerator.open();
    }

    const pushTable = (saleMastersGrouped, doc) => {
        // Convert object to array
        const groups = Object.values(saleMastersGrouped);

        groups.forEach(group => {
            doc.content.push({
                text: `Bill Category : ${group.head_name}`,
                alignment: 'center',
                margin: [0, 0, 20, 10],
                bold: true,
                color: 'black',
                fontSize: 14
            });

            group.sale_masters.forEach(saleMaster => {
                const stack = [
                    {
                        text: `Invoice No: ${saleMaster.bill_number} | Inv Date: ${saleMaster.date} | Bill created by: ${saleMaster.created_user.name}`,
                        margin: [0, 5, 0, 5]
                    },
                    {
                        style: 'tableExample',
                        table: {
                            widths: ['*', '*', 'auto', 'auto', 'auto'],
                            body: getEachInvoiceTable(saleMaster.generals)
                        },
                        layout: {
                            fillColor: (rowIndex) => rowIndex === 0 ? '#f3f2f7' : null,
                            hLineWidth: () => 0.5,
                            vLineWidth: () => 0.5
                        }
                    },
                    {
                        text: getTotalText(saleMaster),
                        margin: [0, 5, 0, 10]
                    }
                ];

                doc.content.push(stack);
            });
        });
    }

    const getSummaryTable = (receiptGenerals, total) => {
        return [
            {
                columns: [
                    {
                        width: '60%',
                        margin: [0, 45, 0, 0],
                        table: {
                            body: getPaidBody(total, receiptGenerals),
                        },
                    },

                    {
                        alignment: 'right',
                        style: 'tableExample',
                        margin: [40, 10, 0, 0],
                        table: {
                            widths: ['*', '45%'],
                            body: getSummaryBody(total, receiptGenerals)
                        },
                        layout: {
                            fillColor: function (rowIndex, node, columnIndex) {
                                return (rowIndex === 0) ? '#f3f2f7' : null;
                            },
                            hLineWidth: function (i, node) {
                                return 0.5;
                            },
                            vLineWidth: function (i, node) {
                                return 0.5;
                            }
                        }
                    }
                ]
            },

            receiptGenerals.length > 0 ?
            {
                style: 'tableExample',
                margin: [0, 20, 0, 0],
                table: {
                    widths: ['auto', 'auto', 'auto', 'auto', '*', 'auto'],
                    body: getReceiptTableBody(receiptGenerals)
                },
                layout: {
                    fillColor: function (rowIndex, node, columnIndex) {
                        return (rowIndex === 0) ? '#f3f2f7' : null;
                    },
                    hLineWidth: function (i, node) {
                        return 0.5;
                    },
                    vLineWidth: function (i, node) {
                        return 0.5;
                    }
                }
            } : null,
        ]
    }

    const getReceiptTableBody = (receiptGenerals) => {
        const header = getReceiptTableHead();
        const row = getReceiptTableRow(receiptGenerals);

        return [
            header,
            ...row
        ]
    }

    const getReceiptTableRow = (receiptGenerals) => {
        const data = [];
        receiptGenerals.map((item) => {
            const row = [
                { text: formatDateWithTime(item.created_at) },
                { text: item.voucher_no },
                { text: item.account_head.name },
                { text: item.receipt_reference },
                { text: item.created_user.name },
                { text: commaFormat(item.total_paid_amount), alignment: 'right' }
            ]
            data.push(row);
        })

        const totalRow = [
            { text: 'Total', alignment: 'right', colSpan: 5 }, '', '', '', '', { text: commaFormat(getTotalReceipt(receiptGenerals)), alignment: 'right' }
        ]

        data.push(totalRow);

        return data;
    }

    const getReceiptTableHead = () => {
        return [
            { text: 'MR Date & Time' },
            { text: 'MR No' },
            { text: 'Mode of payment' },
            { text: 'MoP Ref.' },
            { text: 'Receipt by' },
            { text: 'Receipt Amount' , alignment: 'right' }
        ]
    }

    const getTotalReceipt = (receiptGenerals) => receiptGenerals.reduce((total, item) => total + item.total_paid_amount, 0)

    const getNetBillAmount = (total) => total.amount - (total.return_amount || 0) - (total.discount + total.settlement_discount);

    const getPaidBody = (total, receiptGenerals) => {
        const netAmount = getNetBillAmount(total)
        const totalPaidAmount = receiptGenerals.reduce((total, item) => total + item.total_paid_amount, 0);
        return [
            [{ text: `${netAmount <= totalPaidAmount ? 'PAID' : 'DUE'}`, fillColor: '#DBE7F2', fontSize: 18 }],
        ]
    }

    const getSummaryBody = (total, receiptGenerals) => {
        const totalAfterReturn = total.amount - (total.return_amount || 0);
        const netAmount = getNetBillAmount(total)
        const totalPaidAmount = receiptGenerals.reduce((total, item) => total + item.total_paid_amount, 0);
        const billAmount = (netAmount - totalPaidAmount);

        const rows = [
            [{ text: 'Total Amount', alignment: 'right' }, { text: commaFormat(totalAfterReturn), alignment: 'right' }],
        ];
        if (total.discount) rows.push([{ text: '(-) Bill. Discount', alignment: 'right' }, { text: commaFormat(total.discount), alignment: 'right' }]);
        if (total.settlement_discount) rows.push([{ text: '(-) Settle. Discount', alignment: 'right' }, { text: commaFormat(total.settlement_discount), alignment: 'right' }]);
        if (netAmount) rows.push([{ text: 'Net Bill Amount', alignment: 'right' }, { text: commaFormat(netAmount), alignment: 'right' }]);
        if (totalPaidAmount) rows.push([{ text: 'Total Paid', alignment: 'right' }, { text: commaFormat(totalPaidAmount), alignment: 'right' }]);
        if (billAmount) rows.push([{ text: 'Due Amount', alignment: 'right' }, { text: commaFormat(billAmount), alignment: 'right' }]);
        return rows;
    }

    const getTotalText = (item) => {
        const returnAmount = item.return_amount || 0;
        const netBillAmount = item.total_amount - item.total_discount - returnAmount;
        return `Total Amount: ${commaFormat(item.total_amount)}\t Return: ${commaFormat(returnAmount)}\t Settl. Discount: ${commaFormat(item.total_discount)}\t Net Bill Amount: ${commaFormat(netBillAmount)}\t Paid Amount: ${commaFormat(item.paid_amount)}\t	Due Amount: ${netBillAmount - item.paid_amount}`
    }

    const getEachInvoiceTable = (general) => {
        const header = getEachTableHead();
        const row = getEachTableRow(general);

        return [
            header,
            ...row
        ]
    }

    const getEachTableRow = (general) => {
        const data = [];
        general.map((item) => {
            const row = [
                { text: item.product.name },
                { text: item.description },
                { text: item.quantity },
                { text: commaFormat(item.rate), alignment: 'right' },
                { text: commaFormat(item.total_amount), alignment: 'right' }
            ]
            data.push(row);
        })
        return data;
    }

    const getEachTableHead = () => {
        return [
            { text: 'Item' },
            { text: 'Description' },
            { text: 'Qty' },
            { text: 'Unit Price' },
            { text: 'Amount' , alignment: 'right' }
        ]
    }

    const getContent = (ipdRegisterDetails, saleMastersGrouped, barcode, receiptGenerals, total) => {
        return [
            {
                text: 'IPD BILL (Details)',
                style: 'header',
                alignment: 'center',
                margin: [0, 0, 20, 10],
                bold: true,
                color: 'black',
                decoration: 'underline'
            },

            {
                margin: [0, 20, 0, 0],
                alignment: 'justify',
                columns: [
                    {
                        stack: [
                            {
                                svg: barcode
                            },

                            {
                                text: `${ipdRegisterDetails.patient_name ? 'Patient Name: ' + ipdRegisterDetails.patient_name : ''}`
                            },
        
                            {
                                text: `${ipdRegisterDetails.patient_serial_no ? 'Patient ID: ' + ipdRegisterDetails.patient_serial_no : ''}`
                            },
        
                            {
                                text: `${calculateAge(ipdRegisterDetails.birthday)} ${ipdRegisterDetails.gender ? '| ' + capitalize(ipdRegisterDetails.gender) : ''}`
                            },
        
                            {
                                text: `${ipdRegisterDetails.patient_mobile_no ? 'Phone No: ' + ipdRegisterDetails.patient_mobile_no : ''}`
                            },
        
                            {
                                text: `${ipdRegisterDetails.full_address ? 'Patient Address: ' + ipdRegisterDetails.full_address : ''}`
                            }
                        ]
                    },

                    {
                        alignment: 'right',
                        stack: [
                            {
                                text: `${ipdRegisterDetails.admission_no ? 'IPD admission no: ' + ipdRegisterDetails.admission_no : ''}`
                            },

                            {
                                text: `${ipdRegisterDetails.admission_date ? 'Admission date: ' + ipdRegisterDetails.admission_date : ''}`
                            },

                            {
                                text: `${ipdRegisterDetails.department ? 'Admission Dept.: ' + ipdRegisterDetails.department : ''}`
                            },

                            {
                                text: `${ipdRegisterDetails.service_resource_name ? 'Bed/Cabin no: ' + ipdRegisterDetails.service_resource_name : ''}`
                            },

                            {
                                text: `${ipdRegisterDetails.human_resource_name ? 'Consultant: ' + ipdRegisterDetails.human_resource_name : ''}`
                            }
                        ]
                    }
                ]
            },
        ]
    }

    const calculateAge = (birthday) => {
        const { year } = dateOfBarthConvertToAge(birthday)
        return `${year}y`
    }

    const capitalize = (gender) => {
        return gender.charAt(0).toUpperCase() + gender.slice(1);
    }

    return {
        exportToPDF
    }
}

export default ipdStatementDetails;